import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, RefreshCw } from 'lucide-react';
import { SheetRow } from './sheet';
import { SettingsSwitch } from './storeSettingsForm';

/**
 * Κοινά των σελίδων που αποθηκεύουν κάθε αλλαγή αμέσως (Take Away, Delivery, Ρυθμίσεις Σάλας — μέσω
 * patchSettings.ts), στη διάταξη «Φύλλο» (13/10/2026): γραμμή με διακόπτη, «Αποθηκεύτηκε» στην ενότητα
 * που άλλαξε και αποτυχία φόρτωσης. Καμία μπάρα «Αποθήκευση»: δεν μένουν εκκρεμείς αλλαγές.
 */

const SAVED_MS = 2000;

/** Ποια ενότητα αποθηκεύτηκε μόλις (για 2″) και η συνάρτηση που τη δηλώνει. */
export function useSavedFlash(): [string | null, (sectionId: string) => void] {
  const [section, setSection] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = useCallback((sectionId: string) => {
    clearTimeout(timer.current);
    setSection(sectionId);
    timer.current = setTimeout(() => setSection(null), SAVED_MS);
  }, []);
  return [section, flash];
}

/**
 * «Αποθηκεύτηκε» κάτω από την εξήγηση της ενότητας (aside), κοντά σε ό,τι μόλις άλλαξε — ως 13/10 ήταν
 * στην κεφαλίδα της σελίδας. Μένει στο DOM (και άδειο) ώστε ο αναγνώστης οθόνης να ακούει την αλλαγή.
 * Κρατά το ύψος του μόνο δίπλα στις ρυθμίσεις· σε στενό φύλλο (από πάνω τους) άδειο δεν πιάνει χώρο.
 */
export const SavedNote: React.FC<{ show: boolean }> = ({ show }) => {
  const { t } = useTranslation(['settings']);
  return (
    <p role="status" className="flex items-center gap-1.5 min-h-[18px] group-data-[layout=narrow]/sheet:min-h-0 text-xs font-medium text-emerald-700 dark:text-emerald-400">
      {show && (
        <>
          <Check className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
          {t('settings:instant.saved')}
        </>
      )}
    </p>
  );
};

export interface SwitchRowProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  note?: React.ReactNode;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

/** SheetRow με διακόπτη: όνομα ο τίτλος, περιγραφή ο υπότιτλος (και η σημείωση). */
export const SwitchRow: React.FC<SwitchRowProps> = ({ title, subtitle, note, checked, onChange, disabled = false }) => (
  <SheetRow
    title={title}
    subtitle={subtitle}
    note={note}
    control={({ labelId, descriptionId }) => (
      <SettingsSwitch checked={checked} onChange={onChange} disabled={disabled} labelId={labelId} describedById={descriptionId} />
    )}
  />
);

/** Οι ρυθμίσεις δεν φόρτωσαν: κανένας διακόπτης (θα έδειχνε τιμές που δεν ισχύουν), μόνο «Δοκιμάστε ξανά». */
export const LoadFailed: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => {
  const { t } = useTranslation(['common']);
  return (
    <div role="alert" className="flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-2xl border border-red-500/30 bg-red-500/10">
      <AlertTriangle className="w-6 h-6 shrink-0 text-red-600 dark:text-red-400" aria-hidden="true" />
      <p className="flex-1 min-w-0 text-sm font-semibold text-t1">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="inline-flex items-center justify-center gap-2 min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong bg-surface-2 text-t1 hover:bg-surface-3 transition"
      >
        <RefreshCw className="w-4 h-4" aria-hidden="true" />
        {t('common:tryAgain')}
      </button>
    </div>
  );
};
